// The area icon popover's search (docs/61): every emoji with its names in English and German (emojiNames.json, written
// by scripts/emoji-names.mjs from emojibase-data), loaded the first time the popover opens, so the main bundle doesn't
// carry it. A word typed in either language finds the emoji whose name or keywords hold it; the word list of the
// suggestions (core/areaIcons) comes first, so "Fitness" finds 💪 as the area's suggestion does.

import { areaIconSuggestions, iconWords, OFTEN_USED_AREA_EMOJI } from "../core/areaIcons";
import { AREA_ICON_WORDS } from "../core/areaIconWords";

/** The file's shape: [emoji, English names, German names], the names separated by "|", the label first. */
export interface EmojiNames {
  emoji: string[][];
}

export interface EmojiEntry {
  emoji: string;
  /** The label in English and in German, as written (for a screen reader). */
  label: { en: string; de: string };
  /** Each name and keyword of both languages as words, folded as the suggestions fold them: the English first. */
  names: string[][];
  /** Where the German label is in `names`. */
  labelAt: number;
}

/** The most results shown: six rows of eight. */
export const SEARCH_LIMIT = 48;

export function prepareEmoji(file: EmojiNames): EmojiEntry[] {
  return file.emoji.map(([emoji = "", en = "", de = ""]) => {
    const english = en.split("|");
    const german = de ? de.split("|") : [];
    return {
      emoji,
      label: { en: english[0] ?? "", de: german[0] ?? english[0] ?? "" },
      names: [...english, ...german].map((name) => iconWords(name)),
      labelAt: german.length > 0 ? english.length : 0,
    };
  });
}

let loading: Promise<EmojiEntry[]> | undefined;

/** The list, loaded once (its own chunk). */
export function loadEmoji(): Promise<EmojiEntry[]> {
  loading ??= import("./emojiNames.json").then((module) => prepareEmoji(module.default as EmojiNames));
  return loading;
}

/**
 * How well one typed word fits one name, lower is better: the whole name (0), a word of it (1), the start of a word (2),
 * inside a word from three letters on ("schwein" in "meerschweinchen", 3); undefined when it doesn't fit.
 */
function fitName(word: string, name: readonly string[]): number | undefined {
  if (name.length === 1 && name[0] === word) return 0;
  if (name.includes(word)) return 1;
  if (name.some((part) => part.startsWith(word))) return 2;
  if ([...word].length >= 3 && name.some((part) => part.includes(word))) return 3;
  return undefined;
}

/** An emoji without its presentation selector (U+FE0F): the word list and the names write some with, some without. */
const plain = (emoji: string) => emoji.replaceAll("\uFE0F", "");

const CURATED = new Set([...OFTEN_USED_AREA_EMOJI, ...AREA_ICON_WORDS.topics.flatMap((topic) => topic.emoji)].map(plain));

interface Found {
  emoji: string;
  /** The typed words' best fits, added up. */
  score: number;
  /** Whether a label (not only a keyword) holds every word as a word: "rotes Herz" before the hearts tagged "Herz". */
  inLabel: boolean;
  /** One the word list offers for areas (often used, or suggested for some name): ❤️ before the other hearts. */
  curated: boolean;
  index: number;
}

/**
 * The emoji for a search, best first: those whose names hold every typed word, by how well they fit (a label before a
 * keyword, the word list's emoji before the others) and then in Unicode's order. The suggestions for the text as an area's name join
 * them after the whole-word fits, so "Fitness" finds 💪 as an area of that name would. Empty for an empty search.
 */
export function searchEmoji(entries: readonly EmojiEntry[], query: string, limit = SEARCH_LIMIT): string[] {
  const words = iconWords(query);
  if (words.length === 0) return [];
  const found: Found[] = [];
  entries.forEach((entry, index) => {
    let score = 0;
    let inLabel = true;
    for (const word of words) {
      let best: number | undefined;
      for (const name of entry.names) {
        const rank = fitName(word, name);
        if (rank !== undefined && (best === undefined || rank < best)) best = rank;
      }
      if (best === undefined) return;
      score += best;
      const rank = fitName(word, entry.names[0] ?? []) ?? fitName(word, entry.names[entry.labelAt] ?? []);
      if (rank === undefined || rank > 1) inLabel = false;
    }
    found.push({ emoji: entry.emoji, score, inLabel, curated: CURATED.has(plain(entry.emoji)), index });
  });
  areaIconSuggestions(query).forEach((emoji, at) => {
    found.push({ emoji, score: words.length * 1.5, inLabel: false, curated: true, index: -100 + at });
  });
  found.sort((a, b) => a.score - b.score || Number(b.inLabel) - Number(a.inLabel) || Number(b.curated) - Number(a.curated) || a.index - b.index);
  const result: string[] = [];
  const seen = new Set<string>();
  for (const { emoji } of found) {
    if (seen.has(plain(emoji))) continue;
    seen.add(plain(emoji));
    result.push(emoji);
    if (result.length === limit) break;
  }
  return result;
}

/** Each emoji's name in the interface's language, for a screen reader. */
export function emojiLabels(entries: readonly EmojiEntry[], language: "en" | "de"): Map<string, string> {
  return new Map(entries.map((entry) => [entry.emoji, entry.label[language]]));
}
